import type { JSX } from "react";

/**
 * Presentation-grade SVG illustrations for the instructor lectures.
 *
 * Neutral line work uses `currentColor`, so a single SVG renders white on the
 * navy "present" screen and navy in the light presenter window — the parent
 * just sets `color`. Brand accents (cyan / green / amber) are explicit because
 * they read on both backgrounds.
 */

const CYAN = "#2D9BD6";
const INDIGO = "#5461AD";
const PURPLE = "#64479D";
const GREEN = "#3FD68B";
const AMBER = "#FBA84A";
const RED = "#FF6B6B";

// Font classes — `var()` resolves in CSS (a class), but NOT in an SVG
// presentation attribute, so labels must use className, not fontFamily=.
const DISPLAY = "gf-viz-d";
const MONO = "gf-viz-m";

function Frame({ children }: { children: JSX.Element | JSX.Element[] }) {
  return (
    <svg
      viewBox="0 0 460 300"
      fill="none"
      width="100%"
      style={{ display: "block", maxHeight: "100%" }}
      role="img"
    >
      {children}
    </svg>
  );
}

// You'll READ git history, not write it — a window with a diff under a lens.
function Reading() {
  return (
    <Frame>
      <rect x="48" y="46" width="364" height="208" rx="16" stroke="currentColor" strokeWidth="2.4" />
      <line x1="48" y1="84" x2="412" y2="84" stroke="currentColor" strokeWidth="2.4" />
      <circle cx="70" cy="65" r="4" fill="currentColor" opacity="0.5" />
      <circle cx="86" cy="65" r="4" fill="currentColor" opacity="0.5" />
      <circle cx="102" cy="65" r="4" fill="currentColor" opacity="0.5" />
      {/* code/diff lines */}
      <rect x="74" y="108" width="150" height="11" rx="5.5" fill="currentColor" opacity="0.3" />
      <rect x="74" y="132" width="120" height="11" rx="5.5" fill={GREEN} opacity="0.85" />
      <rect x="74" y="156" width="180" height="11" rx="5.5" fill={RED} opacity="0.8" />
      <rect x="74" y="180" width="100" height="11" rx="5.5" fill="currentColor" opacity="0.3" />
      <rect x="74" y="204" width="140" height="11" rx="5.5" fill={GREEN} opacity="0.85" />
      {/* magnifier */}
      <circle cx="326" cy="176" r="44" stroke={CYAN} strokeWidth="4" />
      <line x1="358" y1="208" x2="392" y2="242" stroke={CYAN} strokeWidth="6" strokeLinecap="round" />
    </Frame>
  );
}

// Commits = labelled snapshots over time.
function Snapshots() {
  const xs = [78, 196, 314];
  return (
    <Frame>
      <line x1="60" y1="226" x2="372" y2="226" stroke="currentColor" strokeWidth="2.4" />
      {xs.map((x, i) => (
        <g key={x}>
          {/* snapshot card */}
          <rect x={x - 38} y="78" width="76" height="62" rx="9" stroke="currentColor" strokeWidth="2.2" />
          <circle cx={x - 14} cy="100" r="7" fill={AMBER} opacity="0.9" />
          <path d={`M${x - 30} 130 L${x - 8} 108 L${x + 6} 122 L${x + 16} 112 L${x + 30} 130 Z`} fill={CYAN} opacity="0.55" />
          {/* stem to timeline */}
          <line x1={x} y1="140" x2={x} y2="206" stroke="currentColor" strokeWidth="1.8" opacity="0.5" strokeDasharray="3 4" />
          {/* commit dot */}
          <circle cx={x} cy="226" r="9" fill={[CYAN, INDIGO, PURPLE][i]} />
          <circle cx={x} cy="226" r="9" stroke="currentColor" strokeWidth="2" opacity="0.35" />
        </g>
      ))}
      <path d="M384 226 h28 m0 0 -7 -6 m7 6 -7 6" stroke="currentColor" strokeWidth="2.2" opacity="0.7" strokeLinecap="round" strokeLinejoin="round" />
      <text x="400" y="258" className={MONO} fontSize="14" fill="currentColor" opacity="0.55" textAnchor="middle">time</text>
    </Frame>
  );
}

// A branch splits off main and merges back.
function Branches() {
  return (
    <Frame>
      {/* main */}
      <line x1="56" y1="214" x2="404" y2="214" stroke="currentColor" strokeWidth="2.6" />
      {[92, 214, 372].map((x) => (
        <circle key={x} cx={x} cy="214" r="9" fill="currentColor" />
      ))}
      <text x="60" y="246" className={MONO} fontSize="15" fill="currentColor" opacity="0.7">main</text>
      {/* feature branch */}
      <path d="M150 214 C 188 214 188 110 240 110 L 300 110 C 350 110 350 214 372 214" stroke={CYAN} strokeWidth="2.6" fill="none" />
      {[240, 300].map((x) => (
        <g key={x}>
          <circle cx={x} cy="110" r="9" fill="#fff" stroke={CYAN} strokeWidth="2.6" />
        </g>
      ))}
      <text x="236" y="92" className={MONO} fontSize="15" fill={CYAN}>feature</text>
    </Frame>
  );
}

// Local laptop syncs with the GitHub cloud via push / pull.
function LocalRemote() {
  return (
    <Frame>
      {/* laptop */}
      <rect x="44" y="96" width="150" height="98" rx="10" stroke="currentColor" strokeWidth="2.4" />
      <path d="M34 206 h170 l-12 -12 H46 z" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
      <circle cx="119" cy="118" r="6" fill={CYAN} />
      <line x1="70" y1="142" x2="168" y2="142" stroke="currentColor" strokeWidth="2" opacity="0.4" />
      <line x1="70" y1="162" x2="148" y2="162" stroke="currentColor" strokeWidth="2" opacity="0.4" />
      <text x="119" y="238" className={DISPLAY} fontSize="15" fontWeight="600" fill="currentColor" textAnchor="middle">Your laptop</text>
      {/* cloud */}
      <path d="M300 132 a30 30 0 0 1 60 4 22 22 0 1 1 -4 44 h-52 a26 26 0 0 1 -4 -48 z" stroke="currentColor" strokeWidth="2.4" />
      <text x="332" y="238" className={DISPLAY} fontSize="15" fontWeight="600" fill="currentColor" textAnchor="middle">GitHub</text>
      {/* push */}
      <path d="M210 124 h82 m0 0 -9 -7 m9 7 -9 7" stroke={CYAN} strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <text x="251" y="112" className={MONO} fontSize="14" fill={CYAN} textAnchor="middle">push</text>
      {/* pull */}
      <path d="M292 176 h-82 m0 0 9 -7 m-9 7 9 7" stroke={INDIGO} strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <text x="251" y="200" className={MONO} fontSize="14" fill={INDIGO} textAnchor="middle">pull</text>
    </Frame>
  );
}

// A pull request: your branch → review → merged to main.
function PullRequest() {
  return (
    <Frame>
      {/* your work */}
      <line x1="66" y1="96" x2="66" y2="170" stroke={CYAN} strokeWidth="2.6" />
      <circle cx="66" cy="96" r="8" fill={CYAN} />
      <circle cx="66" cy="170" r="8" fill={CYAN} />
      <text x="66" y="200" className={MONO} fontSize="13" fill="currentColor" opacity="0.7" textAnchor="middle">your work</text>
      <path d="M86 133 h44 m0 0 -8 -6 m8 6 -8 6" stroke="currentColor" strokeWidth="2.2" opacity="0.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {/* review card */}
      <rect x="140" y="84" width="170" height="120" rx="12" stroke="currentColor" strokeWidth="2.4" />
      <text x="158" y="112" className={DISPLAY} fontSize="15" fontWeight="700" fill="currentColor">Pull request</text>
      <line x1="158" y1="126" x2="292" y2="126" stroke="currentColor" strokeWidth="1.6" opacity="0.3" />
      <circle cx="166" cy="150" r="11" fill={GREEN} />
      <path d="M161 150 l4 4 7 -8" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <text x="186" y="155" className={MONO} fontSize="13" fill="currentColor">Approved</text>
      <rect x="158" y="172" width="120" height="9" rx="4.5" fill="currentColor" opacity="0.25" />
      {/* merge to main */}
      <path d="M310 144 h40 m0 0 -8 -6 m8 6 -8 6" stroke={GREEN} strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="356" y1="120" x2="356" y2="168" stroke="currentColor" strokeWidth="2.6" />
      <circle cx="356" cy="144" r="9" fill={GREEN} />
      <text x="356" y="200" className={MONO} fontSize="13" fill="currentColor" opacity="0.7" textAnchor="middle">main</text>
    </Frame>
  );
}

// A conflict: two edits to the same line; git asks which to keep.
function Conflict() {
  return (
    <Frame>
      <rect x="120" y="70" width="220" height="170" rx="12" stroke="currentColor" strokeWidth="2.4" />
      <rect x="140" y="92" width="120" height="10" rx="5" fill="currentColor" opacity="0.25" />
      {/* yours */}
      <rect x="140" y="124" width="180" height="22" rx="6" fill={CYAN} opacity="0.18" stroke={CYAN} strokeWidth="1.6" />
      <text x="150" y="139" className={MONO} fontSize="12" fill={CYAN}>your version</text>
      {/* theirs */}
      <rect x="140" y="156" width="180" height="22" rx="6" fill={INDIGO} opacity="0.18" stroke={INDIGO} strokeWidth="1.6" />
      <text x="150" y="171" className={MONO} fontSize="12" fill={INDIGO}>their version</text>
      <rect x="140" y="200" width="90" height="10" rx="5" fill="currentColor" opacity="0.25" />
      {/* question */}
      <path d="M340 150 h36 m0 0 -8 -6 m8 6 -8 6" stroke={AMBER} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="404" cy="150" r="24" fill={AMBER} />
      <text x="404" y="160" className={DISPLAY} fontSize="28" fontWeight="700" fill="#3A2A00" textAnchor="middle">?</text>
      <text x="230" y="58" className={MONO} fontSize="13" fill="currentColor" opacity="0.6" textAnchor="middle">same line — which do you keep?</text>
    </Frame>
  );
}

// The safety net: step back to any earlier snapshot.
function SafetyNet() {
  const xs = [70, 160, 250, 340];
  return (
    <Frame>
      <line x1="60" y1="180" x2="350" y2="180" stroke="currentColor" strokeWidth="2.6" />
      {xs.map((x, i) => (
        <circle key={x} cx={x} cy="180" r="9" fill={i === xs.length - 1 ? CYAN : "currentColor"} />
      ))}
      <text x="340" y="212" className={MONO} fontSize="13" fill={CYAN} textAnchor="middle">you are here</text>
      {/* back arrow */}
      <path d="M340 162 C 320 96 180 96 160 158" stroke={CYAN} strokeWidth="2.6" fill="none" strokeLinecap="round" />
      <path d="M160 158 l-7 -10 m7 10 11 -4" stroke={CYAN} strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <text x="250" y="92" className={MONO} fontSize="14" fill="currentColor" opacity="0.75" textAnchor="middle">revert · reset</text>
      {/* net */}
      <path d="M50 232 q150 40 360 0" stroke="currentColor" strokeWidth="1.6" opacity="0.25" fill="none" strokeDasharray="4 6" />
    </Frame>
  );
}

// Three numbered course chips — the hand-off slide.
function Chips({
  items,
  accents,
}: {
  items: { n: string; label: string }[];
  accents: string[];
}) {
  const w = 124;
  const gap = 18;
  const startX = (460 - (w * 3 + gap * 2)) / 2;
  return (
    <Frame>
      <>
        {items.map((it, i) => {
          const x = startX + i * (w + gap);
          return (
            <g key={it.n}>
              <rect x={x} y="74" width={w} height="150" rx="14" stroke="currentColor" strokeWidth="2.4" />
              <text x={x + w / 2} y="132" className={DISPLAY} fontSize="46" fontWeight="700" fill={accents[i]} textAnchor="middle">{it.n}</text>
              <text x={x + w / 2} y="170" className={DISPLAY} fontSize="13.5" fontWeight="600" fill="currentColor" textAnchor="middle">
                {it.label.split("\n").map((line, li) => (
                  <tspan key={li} x={x + w / 2} dy={li === 0 ? 0 : 17}>{line}</tspan>
                ))}
              </text>
              {i < items.length - 1 && (
                <path d={`M${x + w + 2} 149 h${gap - 4} m0 0 -6 -5 m6 5 -6 5`} stroke="currentColor" strokeWidth="2" opacity="0.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              )}
            </g>
          );
        })}
      </>
    </Frame>
  );
}

function Track123() {
  return (
    <Chips
      accents={[CYAN, INDIGO, PURPLE]}
      items={[
        { n: "1", label: "Plain\nEnglish" },
        { n: "2", label: "Commits &\nbranches" },
        { n: "3", label: "Remote\nvs local" },
      ]}
    />
  );
}

function Track456() {
  return (
    <Chips
      accents={[INDIGO, PURPLE, CYAN]}
      items={[
        { n: "4", label: "Pull\nrequests" },
        { n: "5", label: "Merge\nconflicts" },
        { n: "6", label: "Undoing &\nrecovering" },
      ]}
    />
  );
}

// What you've already learned — the recap slide.
function Recap() {
  const rows = ["Snapshots (commits)", "Branches", "Sync with GitHub"];
  return (
    <Frame>
      <>
        {rows.map((label, i) => {
          const y = 84 + i * 64;
          return (
            <g key={label}>
              <rect x="70" y={y} width="320" height="48" rx="24" stroke="currentColor" strokeWidth="2.2" />
              <circle cx="102" cy={y + 24} r="14" fill={GREEN} />
              <path d={`M95 ${y + 24} l5 5 9 -10`} stroke="#fff" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <text x="132" y={y + 30} className={DISPLAY} fontSize="17" fontWeight="600" fill="currentColor">{label}</text>
            </g>
          );
        })}
      </>
    </Frame>
  );
}

export const LECTURE_VISUALS: Record<string, () => JSX.Element> = {
  reading: Reading,
  snapshots: Snapshots,
  branches: Branches,
  "local-remote": LocalRemote,
  "track-123": Track123,
  recap: Recap,
  "pull-request": PullRequest,
  conflict: Conflict,
  "safety-net": SafetyNet,
  "track-456": Track456,
};
